import { useEffect, useRef } from 'react';
import { Application } from 'pixi.js';

/**
 * Cleanup returned by an {@link OnReady} callback, run just before the
 * Application is destroyed (remove ticker callbacks, renderer listeners, ...).
 */
export type SceneCleanup = () => void;

export type OnReady = (
  app: Application,
  host: HTMLDivElement,
) => void | SceneCleanup;

/**
 * Owns a PixiJS v8 Application's lifecycle inside a React container div.
 *
 * The returned ref must be attached to a positioned host element
 * (`position: relative; width: 100%; height: 100%`). On mount it creates and
 * async-inits an Application, appends `app.canvas` to the host, then calls
 * `onReady(app, host)` exactly once. On unmount it runs the onReady cleanup,
 * stops the ticker and destroys the Application (removing the canvas).
 *
 * `onReady` MUST be stable across renders (wrap your scene-build logic so it
 * reads live state through refs). It is captured once per mount; later identity
 * changes are ignored so the Pixi scene is never rebuilt mid-life.
 *
 * Safe against React 18/19 StrictMode double-invoke and against the unmount
 * racing the async `init()` — a cancelled flag guarantees at most one live
 * canvas and immediate teardown if we unmount before init resolves.
 */
export function usePixiScene(onReady: OnReady) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    let cancelled = false;
    let app: Application | null = null;
    let cleanup: SceneCleanup | void;

    // Pixi v8's resizeTo only re-measures on window 'resize'. When the host grows
    // from a layout change with no window event (responsive height, mobile dvh
    // shift as the URL bar hides, HMR'd CSS), the canvas stays its old size and the
    // scene never re-fits. Observe the host and push an app.resize() → renderer
    // 'resize' → each scene's onResize re-fit. rAF-coalesced against scroll bursts.
    let raf = 0;
    const ro = new ResizeObserver(() => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        app?.resize();
      });
    });
    ro.observe(host);

    const start = async () => {
      const instance = new Application();
      await instance.init({
        resizeTo: host,
        backgroundAlpha: 0,
        antialias: true,
        autoDensity: true,
        resolution: Math.min(window.devicePixelRatio || 1, 2),
      });

      // Unmounted (or a StrictMode re-invoke superseded us) while awaiting init:
      // tear the just-created instance down so we never leak an orphan canvas.
      if (cancelled) {
        instance.destroy(
          { removeView: true },
          { children: true, texture: true, textureSource: true },
        );
        return;
      }

      app = instance;
      host.appendChild(instance.canvas);
      cleanup = onReadyRef.current(instance, host);
    };

    void start();

    return () => {
      cancelled = true;
      ro.disconnect();
      if (raf) cancelAnimationFrame(raf);
      if (cleanup) cleanup();
      if (app) {
        app.ticker.stop();
        app.destroy(
          { removeView: true },
          { children: true, texture: true, textureSource: true },
        );
        app = null;
      }
    };
  }, []);

  return hostRef;
}
